/**
 * Consent-based custom tracking behavior
 * - sends consent events based on customer preferences
 * - sends custom events from interactive elements with certain data attributes
 */
export default class InitializeCustomerAnalytics {
  constructor(_, props) {
    this.hasGTM = props.has_gtm;

    Shopify.loadFeatures(
      [
        {
          name: "consent-tracking-api",
          version: "0.1",
        },
      ],
      (error) => {
        if (error) {
          console.error(error);
          return;
        }
        // customerPrivacy API is now loaded

        // check if user has consented or is in a region that doesn't require consent
        const canBeTracked = Shopify.customerPrivacy.userCanBeTracked();

        // if tracking allowed, listen for click events
        if (canBeTracked) {
          this.bindClickEvents();
          // otherwise wait for consent event to occur first
        } else {
          this.bindConsentCollectedEvent();
        }

        this.sendConsentEvent("default");
      },
    );
  }

  bindConsentCollectedEvent() {
    document.addEventListener(
      "visitorConsentCollected",
      this.handleConsentCollected,
    );
  }

  handleConsentCollected = (event) => {
    const { analyticsAllowed } = event.detail;

    if (analyticsAllowed) {
      this.bindClickEvents();
    }

    this.sendConsentEvent("update");
  };

  toGtagConsentValue(boolValue) {
    return boolValue ? "granted" : "denied";
  }

  sendConsentEvent(type) {
    if (!this.hasGTM) return;

    if (!window.gtag || typeof window.gtag !== "function") return;

    const canSellData = Shopify.customerPrivacy.saleOfDataAllowed();
    const analyticsAllowed =
      Shopify.customerPrivacy.analyticsProcessingAllowed();
    const marketingAllowed = Shopify.customerPrivacy.marketingAllowed();

    const consentState = {
      ad_storage: this.toGtagConsentValue(marketingAllowed),
      analytics_storage: this.toGtagConsentValue(analyticsAllowed),
      ad_user_data: this.toGtagConsentValue(canSellData),
      ad_personalization: this.toGtagConsentValue(marketingAllowed),
    };

    // https://developers.google.com/tag-platform/security/concepts/consent-mode
    window.gtag("consent", type, consentState);
  }

  bindClickEvents() {
    if (!window.gtag || typeof window.gtag !== "function") return;

    document.addEventListener("click", this.handleClick);
  }

  handleClick = (event) => {
    const target = event?.target?.closest("[data-verso-track=true]");

    if (!target || !(target instanceof HTMLAnchorElement)) return;

    this.createCustomEvent(target);
  };

  getSourceName(target) {
    const sourceBlock = target.closest("[data-verso-track-source-name]");

    if (!sourceBlock || !(sourceBlock instanceof HTMLElement)) return undefined;

    return sourceBlock?.dataset.versoTrackSourceName;
  }

  createCustomEvent(target) {
    const sourceName = this.getSourceName(target);

    window.gtag("event", "link_click", {
      source_name: sourceName ?? "",
      source_url: window.location.pathname,
      destination_name: target.dataset.versoTrackDestinationName ?? "",
      destination_url: new URL(target.href)?.pathname ?? "",
    });
  }
}
